import React from 'react';
import { Edit, ExternalLink, FlaskRound as Flask } from 'lucide-react';
import { Link } from 'react-router-dom';
import Badge from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { useInventory } from '../../context/InventoryContext';

interface RDProduct {
  id: string;
  name: string;
  description: string;
  category_id: string;
  stock_quantity: number;
  min_stock_level: number;
  image: string;
  sku: string;
  location: string;
  status: 'in_development' | 'testing' | 'prototype' | 'ready' | 'discontinued';
  project_code: string;
  researcher: string;
  budget_allocated?: number;
  estimated_value?: number;
  created_at: string;
  updated_at: string;
}

interface RDProductCardProps {
  product: RDProduct;
}

const RDProductCard: React.FC<RDProductCardProps> = ({ product }) => {
  const { getCategoryName } = useInventory();
  const categoryName = getCategoryName(product.category_id);
  const lowStock = product.stock_quantity <= product.min_stock_level;

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'in_development': return 'primary';
      case 'testing': return 'warning';
      case 'prototype': return 'secondary';
      case 'ready': return 'success';
      case 'discontinued': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_development': return 'In Development';
      case 'testing': return 'Testing';
      case 'prototype': return 'Prototype';
      case 'ready': return 'Ready';
      case 'discontinued': return 'Discontinued';
      default: return status;
    }
  };

  return (
    <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-md group">
      <Link to={`/rd-inventory/${product.id}`} className="flex-grow">
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain bg-gray-50 group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <Flask className="h-12 w-12 text-gray-600" />
            </div>
          )}
          <div className="absolute top-2 right-2 space-x-1">
            {lowStock && (
              <Badge variant="danger">Low Stock</Badge>
            )}
            <Badge variant={getStatusBadgeVariant(product.status)}>
              {getStatusLabel(product.status)}
            </Badge>
          </div>
        </div>
        <CardContent className="flex-1 flex flex-col p-4">
          <div className="mb-2">
            <Badge variant="secondary">{categoryName}</Badge>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600">
            {product.name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2 mb-2">{product.description}</p>
          <div className="mt-auto">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-500">SKU: {product.sku}</span>
              <span className="text-lg font-bold text-blue-900">${(product.estimated_value || 0).toFixed(2)}</span>
            </div>
            
            <div className="mb-3 space-y-1">
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Project:</span>
                <span className="ml-1 text-blue-600">{product.project_code}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Researcher:</span>
                <span className="ml-1 truncate">{product.researcher}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Location:</span>
                <span className="ml-1 truncate">{product.location}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Stock:</span>
              <div className="flex items-center">
                <span className={`${lowStock ? 'text-red-600 font-bold' : 'text-gray-900'} text-lg font-bold`}>
                  {product.stock_quantity}
                </span>
                <span className="text-sm text-gray-500 ml-1">/ {product.min_stock_level} min</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="p-2 bg-blue-50 rounded text-center">
                <div className="text-sm font-bold text-blue-900">${product.budget_allocated?.toLocaleString() || '0'}</div>
                <div className="text-xs text-blue-600">Budget</div>
              </div>
              <div className="p-2 bg-green-50 rounded text-center">
                <div className="text-sm font-bold text-green-900">${product.estimated_value?.toLocaleString() || '0'}</div>
                <div className="text-xs text-green-600">Est. Value</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Link>
      <div className="px-4 pb-4">
        <Link to={`/rd-inventory/${product.id}`} className="flex-1">
          <Button variant="secondary" fullWidth>
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default RDProductCard;